// MarketCalendarPage — Economic events calendar
// Layout:
//   - Top nav: < Back | "Market calendar"
//   - Hero: today + 3 stats
//   - Day / week toggle
//   - Calendar with event pins
//   - Event list with impact, forecast, actual, prior

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Event {
  id: string;
  date: string;
  time: string;
  title: string;
  currency: 'USD' | 'EUR' | 'GBP' | 'JPY';
  impact: 1 | 2 | 3;
  forecast: string;
  actual?: string;
  prior: string;
  description: string;
}

const TODAY = '2026-07-24';
const EVENTS: Event[] = [
  { id: 'e1', date: TODAY, time: '08:30', title: 'Initial Jobless Claims',             currency: 'USD', impact: 2, forecast: '220K', actual: '218K', prior: '222K', description: 'Weekly new unemployment claims' },
  { id: 'e2', date: TODAY, time: '10:00', title: 'Existing Home Sales (MoM)',          currency: 'USD', impact: 2, forecast: '0.5%', prior: '-0.7%', description: 'Monthly existing home sales' },
  { id: 'e3', date: TODAY, time: '14:00', title: 'Fed Chair Powell Speaks',           currency: 'USD', impact: 3, forecast: '—',    prior: '—', description: 'Testimony before Senate' },
  { id: 'e4', date: '2026-07-25', time: '08:30', title: 'Core Durable Goods Orders (MoM)',  currency: 'USD', impact: 2, forecast: '0.3%', prior: '0.1%', description: 'Excludes defense, aircraft' },
  { id: 'e5', date: '2026-07-25', time: '08:30', title: 'GDP (QoQ, advance)',                currency: 'USD', impact: 3, forecast: '2.0%', prior: '1.4%', description: 'Initial estimate' },
  { id: 'e6', date: '2026-07-25', time: '09:55', title: 'Michigan Consumer Sentiment',      currency: 'USD', impact: 1, forecast: '66.5', prior: '66.0', description: 'Final July reading' },
  { id: 'e7', date: '2026-07-26', time: '08:30', title: 'Core PCE (YoY)',                   currency: 'USD', impact: 3, forecast: '2.5%', prior: '2.6%', description: 'Fed\'s preferred inflation gauge' },
  { id: 'e8', date: '2026-07-26', time: '08:30', title: 'Personal Spending (MoM)',          currency: 'USD', impact: 2, forecast: '0.3%', prior: '0.5%', description: 'Nominal consumer outlays' },
  { id: 'e9', date: '2026-07-29', time: '10:00', title: 'CB Consumer Confidence',           currency: 'USD', impact: 2, forecast: '101.0', prior: '100.4', description: 'Conference Board index' },
];

const CURRENCY_COLOR: Record<Event['currency'], string> = {
  USD: '#34C759', EUR: '#007AFF', GBP: '#FF9500', JPY: '#FF3B30',
};

export function MarketCalendarPage() {
  const nav = useNavigate();
  const [view, setView] = useState<'today' | 'week'>('today');
  const today = new Date(TODAY);
  const dayMs = 86400000;

  const todayEvents = EVENTS.filter((e) => e.date === TODAY);
  const weekEvents = EVENTS.filter((e) => {
    const d = new Date(e.date).getTime();
    return d >= today.getTime() && d < today.getTime() + 7 * dayMs;
  });

  const visible = view === 'today' ? todayEvents : weekEvents;

  // Group by date
  const byDate = visible.reduce<Record<string, Event[]>>((acc, e) => {
    (acc[e.date] = acc[e.date] || []).push(e);
    return acc;
  }, {});

  // Count high-impact today
  const highImpactToday = todayEvents.filter((e) => e.impact === 3).length;

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Market calendar</div>
        <button className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 9h18M8 3v4M16 3v4" strokeLinecap="round" /></svg>
        </button>
      </div>

      {/* Hero — today's events */}
      <div className="mx-4 mt-4">
        <div className="ios26-hero text-center">
          <div className="text-[11px] uppercase tracking-wider text-tf-text-3">Today · {today.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</div>
          <div className="mt-1 dynamic-large text-tf-text-1 tabular-nums">{todayEvents.length}</div>
          <div className="mt-1 text-[13px] text-tf-text-3">events · {highImpactToday} high-impact</div>
        </div>
      </div>

      {/* View toggle */}
      <div className="mx-4 mt-3 ios26-seg" data-testid="view-toggle">
        <button onClick={() => setView('today')} className={view === 'today' ? 'active' : ''}>Today</button>
        <button onClick={() => setView('week')} className={view === 'week' ? 'active' : ''}>This week</button>
      </div>

      {/* Mini week calendar */}
      {view === 'week' && (
        <div className="mx-4 mt-3 grid grid-cols-7 gap-1" data-testid="week-strip">
          {Array.from({ length: 7 }).map((_, i) => {
            const d = new Date(today.getTime() + i * dayMs);
            const isToday = d.toDateString() === today.toDateString();
            const dayName = d.toLocaleDateString('en-US', { weekday: 'narrow' });
            const dayNum = d.getDate();
            const dayEvents = EVENTS.filter((e) => e.date === d.toISOString().slice(0, 10));
            const hasHighImpact = dayEvents.some((e) => e.impact === 3);
            return (
              <div
                key={i}
                data-testid={`week-day-${i}`}
                className={['flex flex-col items-center justify-center rounded-[10px] py-2.5 transition', isToday ? 'bg-tf-blue text-white' : 'bg-white border border-tf-border'].join(' ')}
              >
                <div className="text-[10px] uppercase font-semibold opacity-80">{dayName}</div>
                <div className="text-[16px] font-bold tabular-nums">{dayNum}</div>
                <div className="mt-0.5 flex items-center gap-0.5">
                  {dayEvents.slice(0, 3).map((e) => (
                    <span key={e.id} className={['h-1 w-1 rounded-full', e.impact === 3 ? 'bg-[#FF3B30]' : e.impact === 2 ? 'bg-[#FF9500]' : 'bg-[#34C759]'].join(' ')} />
                  ))}
                </div>
                {hasHighImpact && !isToday && <div className="absolute -mt-3 h-1 w-1 rounded-full bg-[#FF3B30]" />}
              </div>
            );
          })}
        </div>
      )}

      {/* Event list by day */}
      {Object.entries(byDate).map(([date, events]) => {
        const d = new Date(date);
        return (
          <div key={date}>
            <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">
              {d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
            </h2>
            <div className="mx-4 ios26-list bg-white">
              {events.map((e, i) => (
                <div
                  key={e.id}
                  data-testid={`event-${e.id}`}
                  className={['flex items-start gap-3 px-4 py-3', i < events.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}
                >
                  <div className="flex flex-col items-center justify-center w-12 shrink-0">
                    <div className="text-[11px] text-tf-text-3 tabular-nums">{e.time}</div>
                    <div className="text-[10px] font-bold tabular-nums" style={{ color: CURRENCY_COLOR[e.currency] }}>{e.currency}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-1.5">
                      <span className="text-[14px] font-semibold text-tf-text-1">{e.title}</span>
                      <div className="flex items-center gap-0.5 mt-0.5 shrink-0">
                        {e.impact === 3 && <><div className="h-2 w-1 rounded bg-[#FF3B30]" /><div className="h-2 w-1 rounded bg-[#FF3B30]" /><div className="h-2 w-1 rounded bg-[#FF3B30]" /></>}
                        {e.impact === 2 && <><div className="h-2 w-1 rounded bg-[#FF9500]" /><div className="h-2 w-1 rounded bg-[#FF9500]" /><div className="h-2 w-1 rounded bg-[#E5E5EA]" /></>}
                        {e.impact === 1 && <><div className="h-2 w-1 rounded bg-[#34C759]" /><div className="h-2 w-1 rounded bg-[#E5E5EA]" /><div className="h-2 w-1 rounded bg-[#E5E5EA]" /></>}
                      </div>
                    </div>
                    <div className="mt-0.5 text-[11px] text-tf-text-3">{e.description}</div>
                    <div className="mt-1.5 flex items-center gap-2 text-[11px]">
                      <span className="text-tf-text-3">F</span>
                      <span className="font-semibold tabular-nums text-tf-text-1">{e.forecast}</span>
                      {e.actual && (
                        <>
                          <span className="text-tf-text-3">· A</span>
                          <span className={['font-semibold tabular-nums', parseFloat(e.actual) > parseFloat(e.forecast) ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>{e.actual}</span>
                        </>
                      )}
                      <span className="text-tf-text-3">· P</span>
                      <span className="text-tf-text-3 tabular-nums">{e.prior}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
